/**
 * The interview state machine and its sessionStorage persistence.
 *
 * SPEC section 6: a refresh mid-interview restores from sessionStorage and
 * resumes at the same question. Nothing is persisted server-side before the
 * gate, so this store is the only record of the conversation.
 */

import { useCallback, useEffect, useReducer } from "react";

import type { DiagnosticAnswer, ReportResponse } from "@/lib/api";
import { logger } from "@/lib/logger";

const ANSWER_SOURCES: readonly string[] = ["chip", "model", "heuristic"];
const REPORT_SOURCES: readonly string[] = ["model", "fallback"];
const LEAD_STATUSES: readonly string[] = ["queued", "submitted", "failed", "skipped"];

export type Phase = "intro" | "interview" | "result" | "report";

/**
 * The only parts of the gate form kept after submission: the first name the
 * report greets, and the company the poster is titled with. No email, no role,
 * no country — nothing the report or the poster does not put on screen.
 */
export interface LeadSummary {
  firstName: string;
  /** Optional so a session saved before the poster existed still restores. */
  company?: string;
}

export interface DiagnosticState {
  phase: Phase;
  /** The question being asked, 0 based. Stays at the last index once finished. */
  step: number;
  answers: DiagnosticAnswer[];
  /** Set with the report, never before: nothing about the lead is kept earlier. */
  lead?: LeadSummary;
  report?: ReportResponse;
}

export type DiagnosticAction =
  | { type: "start" }
  | { type: "answer"; answer: DiagnosticAnswer; questionCount: number }
  | { type: "report"; lead: LeadSummary; report: ReportResponse }
  | { type: "reset" };

export const STORAGE_KEY = "hw-diagnostic-v1";
export const MAX_ANSWER_CHARS = 1500;
/** Show the character counter from here on (SPEC section 6). */
export const COUNTER_FROM = 1200;

/**
 * Whether an answer can be sent at all.
 *
 * The chip carries the score, and the free text is context the model reads on
 * top of it, so a single-select question needs a chip no matter what is typed.
 * The multi-select question is the exception: ticking nothing is a real answer
 * there ("none of these exist"), so it can always be continued.
 *
 * Both the composer button and the send path go through this, so the rule the
 * UI shows and the rule the code enforces cannot drift apart.
 */
export function canSendAnswer(multi: boolean, hasChips: boolean): boolean {
  return multi || hasChips;
}

export const initialState: DiagnosticState = { phase: "intro", step: 0, answers: [] };

export function diagnosticReducer(
  state: DiagnosticState,
  action: DiagnosticAction,
): DiagnosticState {
  switch (action.type) {
    case "start":
      return { phase: "interview", step: 0, answers: [] };

    case "answer": {
      const answers = [...state.answers, action.answer];
      const done = answers.length >= action.questionCount;
      return {
        phase: done ? "result" : "interview",
        step: done ? state.step : state.step + 1,
        answers,
      };
    }

    case "report":
      // Only reachable from the result, where the interview is already whole,
      // so `step` and `answers` carry over untouched.
      return {
        ...state,
        phase: "report",
        lead: action.lead,
        report: action.report,
      };

    case "reset":
      return initialState;

    default:
      return state;
  }
}

/** A field the generated `DiagnosticAnswer` marks optional: absent or a string. */
function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === "string";
}

/**
 * Narrow one restored answer to the generated `DiagnosticAnswer`.
 *
 * `chips`, `text`, `evidence` and `reflection` are optional on that model, so
 * an absent one is accepted and a wrongly typed one is not. `track` and
 * `score` are typed as plain numbers there; this is stricter on purpose, since
 * a track the content does not have would draw a band nobody can explain.
 */
function isAnswer(value: unknown): value is DiagnosticAnswer {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Partial<DiagnosticAnswer>;
  return (
    typeof candidate.qid === "string" &&
    isOptionalString(candidate.text) &&
    isOptionalString(candidate.evidence) &&
    isOptionalString(candidate.reflection) &&
    (candidate.track === 0 || candidate.track === 1 || candidate.track === 2) &&
    typeof candidate.score === "number" &&
    Number.isInteger(candidate.score) &&
    candidate.score >= 0 &&
    candidate.score <= 3 &&
    typeof candidate.source === "string" &&
    ANSWER_SOURCES.includes(candidate.source) &&
    (candidate.chips === undefined ||
      (Array.isArray(candidate.chips) && candidate.chips.every((chip) => typeof chip === "string")))
  );
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

/** One restored track diagnosis: a known track id and its prose. */
function isReportTrack(value: unknown): boolean {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    (candidate.track === 0 || candidate.track === 1 || candidate.track === 2) &&
    typeof candidate.diagnosis === "string"
  );
}

/**
 * Narrow a restored report to the generated `ReportResponse`.
 *
 * The three-per-track arrays are checked by length as well as by type: the
 * report renders one row per track with its score beside it, so a short array
 * would print a blank score rather than fail.
 */
function isReport(value: unknown): value is ReportResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate.tracks) &&
    candidate.tracks.length === 3 &&
    candidate.tracks.every(isReportTrack) &&
    isStringArray(candidate.moves) &&
    typeof candidate.closing === "string" &&
    Array.isArray(candidate.per_track) &&
    candidate.per_track.length === 3 &&
    candidate.per_track.every((score) => typeof score === "number" && Number.isFinite(score)) &&
    typeof candidate.index === "number" &&
    Number.isFinite(candidate.index) &&
    typeof candidate.level === "string" &&
    typeof candidate.weakest_track === "number" &&
    typeof candidate.source === "string" &&
    REPORT_SOURCES.includes(candidate.source) &&
    typeof candidate.lead_status === "string" &&
    LEAD_STATUSES.includes(candidate.lead_status)
  );
}

/** The saved lead: a first name to greet the report with, and the company. */
function isLead(value: unknown): value is LeadSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const { firstName, company } = value as Record<string, unknown>;
  return (
    typeof firstName === "string" &&
    firstName.trim().length > 0 &&
    (company === undefined || typeof company === "string")
  );
}

/**
 * Narrow a parsed session, enforcing the invariants the reducer maintains.
 *
 * The UI cannot render a state the reducer would never produce: a fractional
 * step has no question, and a `result` with two answers renders a wrong index,
 * so anything off the happy path is rejected here rather than halfway down the
 * tree. Mirrors `diagnosticReducer`: during the interview `step` is the number
 * of answers so far; at the result `step` rests on the last question index.
 */
function validate(parsed: unknown): DiagnosticState | null {
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const candidate = parsed as Partial<DiagnosticState>;
  const phase = candidate.phase;
  if (phase !== "interview" && phase !== "result" && phase !== "report") {
    return null;
  }
  const { step } = candidate;
  if (typeof step !== "number" || !Number.isInteger(step) || step < 0) {
    return null;
  }
  if (!Array.isArray(candidate.answers) || !candidate.answers.every(isAnswer)) {
    return null;
  }
  const answers: DiagnosticAnswer[] = candidate.answers;
  if (phase === "interview" && step !== answers.length) {
    return null;
  }
  // The result and the report both sit on a finished interview.
  if (phase !== "interview" && answers.length !== step + 1) {
    return null;
  }
  if (phase !== "report") {
    return { phase, step, answers };
  }
  // A report without its lead or its payload would render an empty screen with
  // no way back to the gate, so it is dropped like any other broken session.
  if (!isLead(candidate.lead) || !isReport(candidate.report)) {
    return null;
  }
  return { phase, step, answers, lead: candidate.lead, report: candidate.report };
}

/** Drop the saved session. Safe to call when storage is unavailable. */
export function clearPersisted(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.warn("[diagnostic]", "could not clear the saved session", error);
  }
}

/** Read a persisted session, discarding anything that does not match the shape. */
export function readPersisted(): DiagnosticState | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const restored = validate(JSON.parse(raw));
    if (!restored) {
      logger.warn("[diagnostic]", "discarded a saved session the interview cannot resume");
      clearPersisted();
      return null;
    }
    return restored;
  } catch (error) {
    logger.warn("[diagnostic]", "could not read the saved session", error);
    clearPersisted();
    return null;
  }
}

/** Write the session, or drop it once the state is back at the intro. */
export function persist(state: DiagnosticState): void {
  try {
    if (state.phase === "intro") {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    logger.warn("[diagnostic]", "could not save the session", error);
  }
}

/**
 * The interview state, restored from sessionStorage on mount and written back
 * on every change once the interview has started.
 */
export function useDiagnosticState() {
  const [state, dispatch] = useReducer(diagnosticReducer, initialState, (fallback) => {
    const restored = readPersisted();
    if (restored) {
      logger.debug("[diagnostic]", "restored session", {
        phase: restored.phase,
        step: restored.step,
        answers: restored.answers.length,
      });
      return restored;
    }
    return fallback;
  });

  useEffect(() => {
    persist(state);
  }, [state]);

  const reset = useCallback(() => {
    logger.debug("[diagnostic]", "reset");
    dispatch({ type: "reset" });
  }, []);

  return { state, dispatch, reset };
}
